/* ImageDetailsComponent.js */

"use strict";

const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./ImageDetailsRenderer");
const ImagesFeed = require("./PagingImagesFeed");
const LinksComponent = require("./column_renderers/links/LinksComponent");
const StatusComponent = require("./column_renderers/status/OsLinkStatusComponent");
const EnableComponent = require("./column_renderers/enable/OsLinkEnableComponent");
const HashComponent = require("./column_renderers/hash/OsLinkHashComponent");
const defaultColumnWithTitleComponent = require("./column_renderers/defaultColumnWithTitle/DefaultColumnWithTitleComponent");
const ProgressComponent = require("./column_renderers/upload_progress/UploadProgressComponent");

class ImageDetailsComponent extends EventEmitter {
    constructor(planDetails) {
        super();

        this._planDetails = planDetails;
        this._planDetails.childViewOpen = true;
        this._gameEditor = planDetails.getGameEditor();
        this._modalUserFeedback = planDetails._modalUserFeedback;
        this._activeOrgId = this._gameEditor.activeOrgId;
        this._modalUserFeedback = this._gameEditor._modalUserFeedback;
        this._osPlan = this._gameEditor.getPlan();
        this._osPlanEntityDef = this._gameEditor._planEntity;
        this._isDirty = false;
        this._key = "odmOsLink";
        this._storageFactory = this._gameEditor._storageFactory;
        this._logger = this._gameEditor._logger;
        this._imagesFeed = undefined;
        this._userGridData = undefined;
        this._userId = _.get(this._gameEditor._userSession, "user.id", "");
        this._selectedLinkForUploadImage = undefined;
        this._OdmOsLinkEntity = this._gameEditor._OdmOsLinkEntity;

        this._renderer = new Renderer(this);

        this._registerListeners();
    }

    get columnConfig() {
        return {
            enabled: {
                label: "Enabled",
                width: 70,
                priority: 1,
                columnRendererComponent: EnableComponent,
                entityColumn: true,
                context: {
                    planId: this._osPlan.id,
                    planDetails: this._planDetails,
                    planEntityDef: this._osPlanEntityDef,
                },
                sortable: false,
            },
            name: {
                sort: "asc",
                label: "Name",
                width: 120,
                priority: 2,
                columnRendererComponent: defaultColumnWithTitleComponent,
            },
            description: {
                label: "Description",
                width: 380,
                priority: 3,
                columnRendererComponent: defaultColumnWithTitleComponent,
            },
            url: {
                label: "URL",
                width: 440,
                priority: 4,
                columnRendererComponent: defaultColumnWithTitleComponent,
            },
            size: {
                label: "Size",
                width: 80,
                priority: 5,
            },
            progress: {
                label: "Progress",
                width: 80,
                priority: 8,
                columnRendererComponent: ProgressComponent,
                entityColumn: true,
            },
            hash: {
                label: "Hash",
                width: 320,
                priority: 6,
                columnRendererComponent: HashComponent,
                sortable: false,
            },
            linkStatus: {
                label: "Status",
                width: 140,
                priority: 7,
                columnRendererComponent: StatusComponent,
                context: {linkStatusValues: this._OdmOsLinkEntity.linkStatusLabels},
                entityColumn: true,
            },
            action: {
                label: "Actions",
                width: 75,
                priority: 9,
                columnRendererComponent: LinksComponent,
                context: {gameEditor: this._gameEditor},
                entityColumn: true,
                sortable: false,
                isLockedRight: true,
            },
        };
    }

    whenModelIsReady() {
        return this._storageFactory.whenStorageIsReady().then(() => {
            this._userGridData = this._storageFactory.getRemoteStorageInstanceForEntityGrid(
                "entity_grid_gl",
                this._key
            );

            const userSorting = this._userGridData.getSorting() || {};
            const patchedSorting = this._patchSorting(userSorting);

            this._imagesFeed = new ImagesFeed({
                activeOrgId: this._activeOrgId,
                feedKey: this._key + "_feed",
                userSession: this._userSession,
                entityDef: this._osPlan,
                columnConfig: this.columnConfig,
                additionalDataFields: [
                    "downloadAttempts",
                    "downloadStartedOn",
                    "downloadCompletedOn",
                    "uploadStartedOn",
                    "uploadCompletedOn",
                    "errorMsg",
                ],
                sort: patchedSorting,
            });

            return this._imagesFeed.whenFeedReady();
        });
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get pagingFeed() {
        return this._imagesFeed;
    }

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    get gameEditor() {
        return this.getGameEditor();
    }

    get logger() {
        return this._logger;
    }

    getGameEditor() {
        return this._gameEditor;
    }

    get key() {
        return this._key;
    }

    get isDirty() {
        return this._isDirty;
    }

    set isDirty(newValue) {
        this._isDirty = newValue;
        return true;
    }

    shutdown() {
        return this._backToDetailsClick()
            .then(() => {
                return this._shutdown();
            })
            .catch((err) => {
                this._logger.warn({error: err}, "ImageDetailsComponent error.");
                this._modalUserFeedback.displayError(err);
            });
    }

    _shutdown() {
        this._unregisterListeners();
        return Promise.all([this._imagesFeed.shutdown()])
            .then(() => {
                return this._renderer._shutdown();
            })
            .then(() => {
                this._planDetails = undefined;
                this._gameEditor = undefined;
                this._modalUserFeedback = undefined;
                this._activeOrgId = undefined;
                this._modalUserFeedback = undefined;
                this._osPlan = undefined;
                this._osPlanEntityDef = undefined;
                this._isDirty = undefined;
                this._key = undefined;
                this._storageFactory = undefined;
                this._logger = undefined;
                this._imagesFeed = undefined;
                this._userGridData = undefined;
                this._userId = undefined;
                this._renderer = undefined;
                this._selectedLinkForUploadImage = undefined;
                this._OdmOsLinkEntity = undefined;
                Promise.resolve();
            });
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onDirtyStateChanged(dirtyState) {
        this.emit("Dirty", dirtyState);
    }

    _onUserSorted(sortData) {
        let sort = {};

        const sortCriteria =
            this.columnConfig[sortData.sortColumn].valuePath ||
            this.columnConfig[sortData.sortColumn].path ||
            sortData.sortColumn;

        sort[sortCriteria] = sortData.sort === "asc" ? 1 : -1;
        sort = this._patchSorting(sort);

        this._imagesFeed.setSort(sort);
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    _backToDetailsClick() {
        return Promise.resolve();
    }

    _patchSorting(sorting) {
        // add second sorting order by name
        let patchedSorting = Object.assign({}, sorting);
        if (
            patchedSorting &&
            Object.keys(patchedSorting).length === 1 &&
            !patchedSorting.name &&
            this.columnConfig.name
        ) {
            patchedSorting.name = 1;
        }
        return patchedSorting;
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /*******************************************/
}
module.exports = ImageDetailsComponent;
